/**
 * Generated from https://github.com/mladenilic/threesixty.js due to import issues with ES2022.
 * Generated @12/05/2023 - Version 1.3
 */
export interface ThreeSixtyOptions {
    /**
     * Image source URL or array of image URLs
     */
    image: string | string[];

    /**
     * Sprite options (only used if an array of images is not provided)
     */
    count?: number; // Total number of images. Default: 0
    perRow?: number; // Number of images per row. Default: 0

    /**
     * Image width & height
     */
    width?: number; // Image width. Default: 300
    height?: number; // Image height. Default: 300

    /**
     * Navigation options
     */
    prev?: HTMLElement | null; // Previous button element. Default: null
    next?: HTMLElement | null; // Next button element. Default: null
    keys?: boolean; // Rotate image with arrow keys. Default: true
    draggable?: boolean; // Rotate image by dragging. Default: true
    swipeable?: boolean; // Rotate image by swiping on mobile. Default: true
    dragTolerance?: number; // Rotation speed when dragging. Default: 10
    swipeTolerance?: number; // Rotation speed when swiping. Default: 10
    swipeTarget?: HTMLElement | null; // Element for swipe events. Default: Image container

    /**
     * Rotation speed and inversion
     */
    speed?: number; // Rotation speed during 'play' mode. Default: 100
    inverted?: boolean; // Inverts rotation direction. Default: false

    /**
     * Undocumented, but works.
     */
    aspectRatio?: number; // Set aspect ratio of the images
}

class Events {
    #dragOrigin: number | null = null;
    #options: Required<ThreeSixtyOptions>;
    #eventHandlers: any;

    constructor(
        private threesixty: ThreeSixty,
        options: Required<ThreeSixtyOptions>
    ) {
        this.#options = options;

        this.#eventHandlers = {
            container: {
                mousedown: (e: MouseEvent) => (this.#dragOrigin = e.pageX),
                touchstart: (e: TouchEvent) => (this.#dragOrigin = e.touches[0].clientX),
                touchend: () => (this.#dragOrigin = null),
            },
            prev: {
                mousedown: (e: MouseEvent) => {
                    e.preventDefault();
                    threesixty.play(true);
                },
                mouseup: (e: MouseEvent) => {
                    e.preventDefault();
                    threesixty.stop();
                },
                touchstart: (e: TouchEvent) => {
                    e.preventDefault();
                    threesixty.prev();
                },
            },
            next: {
                mousedown: (e: MouseEvent) => {
                    e.preventDefault();
                    threesixty.play();
                },
                mouseup: (e: MouseEvent) => {
                    e.preventDefault();
                    threesixty.stop();
                },
                touchstart: (e: TouchEvent) => {
                    e.preventDefault();
                    threesixty.next();
                },
            },
            global: {
                mouseup: () => (this.#dragOrigin = null),
                mousemove: (e: MouseEvent) => {
                    if (this.#dragOrigin !== null && Math.abs(this.#dragOrigin - e.pageX) > this.#options.dragTolerance) {
                        threesixty.stop();
                        this.#dragOrigin > e.pageX ? threesixty.prev() : threesixty.next();
                        this.#dragOrigin = e.pageX;
                    }
                },
                touchmove: (e: TouchEvent) => {
                    if (this.#dragOrigin !== null && Math.abs(this.#dragOrigin - e.touches[0].clientX) > this.#options.swipeTolerance) {
                        threesixty.stop();
                        this.#dragOrigin > e.touches[0].clientX ? threesixty.prev() : threesixty.next();
                        this.#dragOrigin = e.touches[0].clientX;
                    }
                },
                keydown: (e: KeyboardEvent) => {
                    if ([37, 39].includes(e.keyCode)) {
                        threesixty.play(37 === e.keyCode);
                    }
                },
                keyup: (e: KeyboardEvent) => {
                    if ([37, 39].includes(e.keyCode)) {
                        threesixty.stop();
                    }
                },
            },
        };

        this._initEvents();
    }

    public destroy() {
        this.#options.swipeTarget?.removeEventListener('mousedown', this.#eventHandlers.container.mousedown);
        this.#options.swipeTarget?.removeEventListener('touchstart', this.#eventHandlers.container.touchstart);
        this.#options.swipeTarget?.removeEventListener('touchend', this.#eventHandlers.container.touchend);

        window.removeEventListener('mouseup', this.#eventHandlers.global.mouseup);
        window.removeEventListener('mousemove', this.#eventHandlers.global.mousemove);
        window.removeEventListener('touchmove', this.#eventHandlers.global.touchmove);
        window.removeEventListener('keydown', this.#eventHandlers.global.keydown);
        window.removeEventListener('keyup', this.#eventHandlers.global.keyup);

        if (this.#options.prev) {
            this.#options.prev.removeEventListener('mousedown', this.#eventHandlers.prev.mousedown);
            this.#options.prev.removeEventListener('mouseup', this.#eventHandlers.prev.mouseup);
            this.#options.prev.removeEventListener('touchstart', this.#eventHandlers.prev.touchstart);
        }

        if (this.#options.next) {
            this.#options.next.removeEventListener('mousedown', this.#eventHandlers.next.mousedown);
            this.#options.next.removeEventListener('mouseup', this.#eventHandlers.next.mouseup);
            this.#options.next.removeEventListener('touchstart', this.#eventHandlers.next.touchstart);
        }
    }

    private _initEvents() {
        if (this.#options.draggable) {
            this.#options.swipeTarget?.addEventListener('mousedown', this.#eventHandlers.container.mousedown);
            window.addEventListener('mouseup', this.#eventHandlers.global.mouseup);
            window.addEventListener('mousemove', this.#eventHandlers.global.mousemove);
        }

        if (this.#options.swipeable) {
            this.#options.swipeTarget?.addEventListener('touchstart', this.#eventHandlers.container.touchstart);
            this.#options.swipeTarget?.addEventListener('touchend', this.#eventHandlers.container.touchend);
            window.addEventListener('touchmove', this.#eventHandlers.global.touchmove);
        }

        if (this.#options.keys) {
            window.addEventListener('keydown', this.#eventHandlers.global.keydown);
            window.addEventListener('keyup', this.#eventHandlers.global.keyup);
        }

        if (this.#options.prev) {
            this.#options.prev.addEventListener('mousedown', this.#eventHandlers.prev.mousedown);
            this.#options.prev.addEventListener('mouseup', this.#eventHandlers.prev.mouseup);
            this.#options.prev.addEventListener('touchstart', this.#eventHandlers.prev.touchstart);
        }

        if (this.#options.next) {
            this.#options.next.addEventListener('mousedown', this.#eventHandlers.next.mousedown);
            this.#options.next.addEventListener('mouseup', this.#eventHandlers.next.mouseup);
            this.#options.next.addEventListener('touchstart', this.#eventHandlers.next.touchstart);
        }
    }
}

export default class ThreeSixty {
    #options: Required<ThreeSixtyOptions>;
    #index = 0;
    #loopTimeoutId: number | null = null;
    #looping = false;
    #maxloops: number | null = null;
    #events: Events;
    #sprite: boolean;

    public nloops = 0;

    constructor(
        public container: HTMLElement,
        options: ThreeSixtyOptions
    ) {
        this.#options = Object.assign(
            {
                width: 300,
                height: 300,
                aspectRatio: 0,
                count: 0,
                perRow: 0,
                speed: 100,
                dragTolerance: 10,
                swipeTolerance: 10,
                draggable: true,
                swipeable: true,
                keys: true,
                inverted: false,
                swipeTarget: null,
                prev: null,
                next: null,
            },
            options
        );

        this.#options.swipeTarget ??= this.container;

        this.#sprite = !Array.isArray(this.#options.image);
        if (!this.#sprite) {
            this.#options.count = (this.#options.image as string[]).length;
        }

        Object.freeze(this.#options);
        this.#events = new Events(this, this.#options);

        this._windowResizeListener = this._windowResizeListener.bind(this);
        this._initContainer();
    }

    public get isResponsive() {
        return this.#options.aspectRatio > 0;
    }

    public get containerWidth() {
        return this.isResponsive ? this.container.clientWidth : this.#options.width;
    }

    public get containerHeight() {
        return this.isResponsive ? this.container.clientWidth * this.#options.aspectRatio : this.#options.height;
    }

    public get index() {
        return this.#index;
    }

    public get looping() {
        return this.#looping;
    }

    public get sprite() {
        return this.#sprite;
    }

    public next() {
        this.goto(this.#options.inverted ? this.#index - 1 : this.#index + 1);
    }

    public prev() {
        this.goto(this.#options.inverted ? this.#index + 1 : this.#index - 1);
    }

    public goto(index: number) {
        this.#index = (this.#options.count + index) % this.#options.count;
        this._update();
    }

    public play(reversed?: boolean, maxloops?: number) {
        if (this.#looping) return;

        this._loop(reversed);
        this.#looping = true;
        this.#maxloops = maxloops ?? null;
        this.nloops = 0;
    }

    public stop() {
        if (!this.#looping) return;

        if (this.#loopTimeoutId !== null) window.clearTimeout(this.#loopTimeoutId);
        this.#looping = false;
        this.#maxloops = null;
        this.nloops = 0;
    }

    public toggle(reversed?: boolean) {
        this.#looping ? this.stop() : this.play(reversed);
    }

    public destroy() {
        this.stop();
        this.#events.destroy();

        this.container.style.width = '';
        this.container.style.height = '';
        this.container.style.backgroundImage = '';
        this.container.style.backgroundPositionX = '';
        this.container.style.backgroundPositionY = '';
        this.container.style.backgroundSize = '';

        if (this.isResponsive) {
            window.removeEventListener('resize', this._windowResizeListener);
        }
    }

    private _loop(reversed?: boolean) {
        reversed ? this.prev() : this.next();

        if (this.#index === 0) {
            this.nloops += 1;
            if (this.#maxloops && this.nloops >= this.#maxloops) {
                this.stop();
                return;
            }
        }

        this.#loopTimeoutId = window.setTimeout(() => this._loop(reversed), this.#options.speed);
    }

    private _update() {
        if (this.#sprite) {
            this.container.style.backgroundPositionX = -(this.#index % this.#options.perRow) * this.containerWidth + 'px';
            this.container.style.backgroundPositionY = -Math.floor(this.#index / this.#options.perRow) * this.containerHeight + 'px';
        } else {
            this.container.style.backgroundImage = `url("${(this.#options.image as string[])[this.#index]}")`;
        }
    }

    private _windowResizeListener() {
        this.container.style.height = this.containerHeight + 'px';
        this._update();
    }

    private _initContainer() {
        if (!this.isResponsive) {
            this.container.style.width = this.containerWidth + 'px';
        }
        this.container.style.height = this.containerHeight + 'px';

        if (this.#sprite) {
            this.container.style.backgroundImage = `url("${this.#options.image}")`;
            const cols = this.#options.perRow;
            const rows = Math.ceil(this.#options.count / this.#options.perRow);
            this.container.style.backgroundSize = cols * 100 + '% ' + rows * 100 + '%';
        }

        if (this.isResponsive) {
            window.addEventListener('resize', this._windowResizeListener);
        }

        this._update();
    }
}
